import React, { useMemo, useState } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import PackageTemplateDrawer from './PackageTemplateDrawer';
import { usePackageTemplates } from '../../data/usePackageTemplates';
import {
  useMoneyFormat, useCurrency, useDateFormat, useDatePattern,
} from '../../context/LocaleContext';
import { clampAmountDecimals } from '../../domain/money';
import {
  applyOptions, saleSteps, convertNotice, convertingLessons, saleAmountProblem,
  saleCanContinue, saleChargeMinor, salePrefillAmount, salePriceMinor, templatePriceMinor,
} from '../../domain/packageSale';
import {
  BLANK_TEMPLATE, formatDuration, templateFormErrors, templateOptionLabel, templateSaveInput,
} from '../../domain/packageTemplates';
import s from './packages.module.css';
import t from '../SimpleTable.module.css';
import { DateInput, DecimalInput } from '../RegionInputs';

// `Add Package` — the three-step sale wizard, as MMS's (#181):
//
//   1. `Package`               student, template, start date — and, once a
//                              template is picked, `Customize Charge`
//   2. `Review & Apply Hours`  the charge, and the `Apply Package Time` radio
//   3. `Confirmation`
//
// A teacher never types a price: it comes off the template. `Customize Charge`
// is the opt-in override, so the common case is not a typed number.
//
// Three MMS details a reader might take for mistakes:
//
//   * `Customize Charge` appears only after a template is picked; step 1 shows
//     two controls until then.
//   * It overrides the price only, and the sale only: no duration override, the
//     template's own price unchanged, and a later sale of the same template
//     prefilled with the template's price again.
//   * Step 2 previews the charge, not the events: a one-row `Item | Time |
//     Price` table and the radio, nothing about which lessons will be
//     converted. `ApplyStep` below is the one place we knowingly say more.
//
// ── Two entry points, one wizard (#377) ───────────────────────────────────
//
// The student rail opens this with a student fixed; `Packages → Package
// Balances` opens it with none, as MMS puts `+ Add Package` on that tab too. So
// step 1 grows a `Student` select inside the wizard, as MMS's step 1 is
// `student, template, start date`.
//
// `studentId` is controlled by the caller: the convertible list is a fold over
// that student's occurrences, prices and attendance statuses, which only the
// caller holds, so the caller must know who is picked before handing the
// lessons over. `onStudentChange` is how it finds out. A caller that fixes a
// student passes no `students` and no handler, and nothing below renders.
//
// A studio-level sale with an empty `convertible` list would be a silent
// failure — step 2 previews the charge and not the events, so a teacher taking
// MMS's destructive default would see nothing wrong — which is why the picker
// and the lessons move together.

// The wizard's decisions — the steps, the override's read and refusal, what
// converts and the sentence counting it, the price the review shows — are
// src/domain/packageSale.js.
const STEPS = () => saleSteps();

export default function SellPackageWizard({
  open, onClose, studentName, studentId, familyId, teacherName = '',
  // Studio-level entry only: the roster to pick from, as
  // `{ id, label }`, and the handler that tells the caller who was
  // picked so it can fold that student's `convertible`. Absent when a student
  // is already fixed — see the header.
  students = null, onStudentChange,
  // Every lesson on this student's calendar that has been marked, would have
  // drawn on package time, and carries a charge — shaped by the caller, the
  // only thing that holds occurrences. The filter is `convertibleLessonsOf` in
  // `src/hooks/useStudentPackageForecast.js`, where its four conditions are
  // stated.
  convertible = [],
  onSell,
  todayISO,
}) {
  const { templates, saveTemplate } = usePackageTemplates();
  const formatMoney = useMoneyFormat();
  const currency = useCurrency();

  const [step, setStep] = useState(0);
  const [templateId, setTemplateId] = useState('');
  const [startOn, setStartOn] = useState(todayISO);
  const [customize, setCustomize] = useState(false);
  const [amount, setAmount] = useState('');
  // MMS's default, and it is the destructive one — see `ApplyStep`.
  const [apply, setApply] = useState(() => applyOptions()[0].id);
  const [saving, setSaving] = useState(false);
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState(BLANK_TEMPLATE);

  const template = templates.find((x) => x.id === templateId) || null;

  // `Existing events` is bounded by the package start date, not by today, as in
  // MMS: a package backdated to 9/1 converts a lesson on 9/15 already taught
  // and charged, and leaves one on 9/5 alone. Past and future are alike on
  // either side of that line, and there is no other window.
  const converting = useMemo(
    () => convertingLessons(convertible, apply, startOn),
    [apply, convertible, startOn],
  );

  const chargeMinor = saleChargeMinor({ customize, amount }, currency);
  // The override is a money field a teacher types into, so it keeps #173's
  // refusal, unlike the template's price box, which copies MMS and strips the
  // minus silently: this one arrives prefilled, and a minus typed into it is
  // an edit to a number that was already right.
  const amountProblem = saleAmountProblem({ customize, amount }, currency);

  const reset = () => {
    setStep(0);
    setTemplateId('');
    setStartOn(todayISO);
    setCustomize(false);
    setAmount('');
    setApply(applyOptions()[0].id);
    // The picked student is not cleared here: it lives in the caller, and
    // clearing it from both sides would be a duplicate no test could tell
    // apart. `PackageBalancesPanel`'s `closeWizard` clears it, or reopening
    // would offer the previous sale's student beside this sale's blank
    // template.
  };

  const close = () => { reset(); onClose?.(); };

  const pickTemplate = (id) => {
    setTemplateId(id);
    // Prefilled from the template, as `Customize Charge` reveals an `Amount`
    // already holding the template's price. Refilled on every change of
    // template, because a stale prefill would be a price the teacher never saw.
    const picked = templates.find((x) => x.id === id);
    setAmount(salePrefillAmount(picked, currency));
  };

  const sell = async () => {
    if (!template || amountProblem) return;
    setSaving(true);
    try {
      await onSell({
        studentId,
        familyId,
        template,
        startOn,
        chargeMinor,
        teacherName,
        convert: converting,
      });
      setStep(2);
    } finally {
      setSaving(false);
    }
  };

  const saveInlineTemplate = async () => {
    if (templateFormErrors(templateForm, currency).refused) return;
    const id = await saveTemplate(templateSaveInput(templateForm, currency));
    setAddingTemplate(false);
    if (id) setTemplateId(id);
  };

  const footer = step === 2 ? (
    <><FooterSpacer /><Button onClick={close}>Done</Button></>
  ) : (
    <>
      <Button variant="secondary" onClick={close}>Cancel</Button>
      <FooterSpacer />
      {step > 0 ? (
        <Button variant="secondary" onClick={() => setStep(step - 1)}>Previous</Button>
      ) : null}
      {step === 0 ? (
        // MMS keeps `Next` disabled until a template is chosen — there is no
        // ad-hoc path and no blank option in the dropdown — and it is legible
        // because the dropdown above is visibly the thing that is empty.
        // `|| !studentId` is the studio-level half (#377): a sale with no
        // student would write a package nobody holds. The student rail always
        // passes one.
        <Button onClick={() => setStep(1)} disabled={!saleCanContinue({ template, studentId })}>Next</Button>
      ) : (
        <Button onClick={sell} loading={saving}>Confirm</Button>
      )}
    </>
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={close}
        title="Add Package"
        steps={STEPS()}
        currentStep={step}
        width={620}
        footer={footer}
      >
        {step === 0 ? (
          <>
            <div className={s.blurb}>
              {studentName
                ? <>Sells a block of prepaid time to {studentName}&rsquo;s family.{' '}</>
                : <>Sells a block of prepaid time to a student&rsquo;s family.{' '}</>}
              Lessons draw on it
              before generating a charge, so the family is not billed twice for the same time.
            </div>

            {/* Studio-level entry only. See the header for why the caller owns
                the value. */}
            {students ? (
              <Field label="Student" htmlFor="sell-student" required>
                <select
                  id="sell-student"
                  className={s.input}
                  value={studentId || ''}
                  onChange={(e) => onStudentChange?.(e.target.value)}
                >
                  <option value="">Select…</option>
                  {students.map((x) => (
                    <option key={x.id} value={x.id}>{x.label}</option>
                  ))}
                </select>
              </Field>
            ) : null}

            <Field label="Package Template" htmlFor="sell-template" required>
              <select
                id="sell-template"
                className={s.input}
                value={templateId}
                onChange={(e) => pickTemplate(e.target.value)}
              >
                <option value="">Select…</option>
                {templates.map((x) => (
                  <option key={x.id} value={x.id}>
                    {templateOptionLabel(x, formatMoney(templatePriceMinor(x)))}
                  </option>
                ))}
              </select>
            </Field>
            {/* `+ Create Package` sits under the select in MMS, the
                managed-list-with-inline-create shape of categories (#110) and
                tags (#138). A studio with no templates cannot get past step 1
                otherwise. */}
            <button
              type="button"
              className={s.inlineLink}
              onClick={() => { setTemplateForm(BLANK_TEMPLATE()); setAddingTemplate(true); }}
            >
              + Create Package
            </button>

            <div className={s.narrow}>
              <Field label="Package Start Date" htmlFor="sell-start-date">
                <DateInput
                  id="sell-start-date"
                  className={s.input}
                  value={startOn}
                  onChange={(e) => setStartOn(e.target.value)}
                />
              </Field>
            </div>

            {/* Only once a template is picked. See the header. */}
            {template ? (
              <>
                <label className={s.toggleRow}>
                  <input
                    type="checkbox"
                    checked={customize}
                    onChange={(e) => setCustomize(e.target.checked)}
                  />
                  Customize Charge
                </label>
                {customize ? (
                  <div className={s.narrow}>
                    <Field
                      label="Amount"
                      error={amountProblem}
                      hint="Changes this sale only — the template keeps its own price."
                    >
                      <DecimalInput
                        className={s.input}
                        aria-label="Amount"
                        value={amount}
                        // Truncates a third decimal at the keystroke rather
                        // than rounding it at save (#308): `sell()` hands this
                        // to `fromDecimal`, which rounds half-up, so `50.999`
                        // would charge $51.00. `clampAmountDecimals` is MMS's
                        // own amount-box editor (#246).
                        //
                        // `clampAmountDecimals` and not `stripMinus`, which the
                        // template's Price box uses: that field swallows a
                        // typed minus because MMS's does, and this one refuses
                        // it through `packagePriceError` (#173). Only the
                        // decimal places are shared.
                        onChange={(e) => setAmount(clampAmountDecimals(e.target.value, currency))}
                      />
                    </Field>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}

        {step === 1 ? (
          <ApplyStep
            template={template}
            startOn={startOn}
            chargeMinor={chargeMinor}
            apply={apply}
            setApply={setApply}
            converting={converting}
            formatMoney={formatMoney}
          />
        ) : null}

        {step === 2 ? (
          <div className={s.blurb}>
            <b>Package Added.</b> A package charge has been added to the student&rsquo;s account.
          </div>
        ) : null}
      </Drawer>

      <PackageTemplateDrawer
        open={addingTemplate}
        form={templateForm}
        onChange={(patch) => setTemplateForm((f) => ({ ...f, ...patch }))}
        onClose={() => setAddingTemplate(false)}
        onSave={saveInlineTemplate}
      />
    </>
  );
}

// Step 2 — `Review & Apply Hours`.
//
// MMS shows a one-row `Item | Time | Price` table with a `Total:`, then the
// `Apply Package Time` radio. The two option strings are theirs verbatim and
// the values are theirs too (`CurrentEvents` / `NewEventsOnly`).
//
// ── The default is destructive, and we copy it (#181) ─────────────────
//
// In MMS, `Existing events` on a package with a backdated start converts each
// lesson from the start date on — already taught, already recorded, already
// carrying a charge on the family account — to package usage, and deletes the
// charge. A lesson before the start date is untouched, and not-billable
// events are skipped (an `Absent, Notice Given` lesson draws nothing). So a
// charge a family may already have been billed for is removed, and MMS says
// nothing. Copied deliberately; it is not a bug.
//
// We say more in one place: the line under the radio counting what it will
// convert. The behaviour is destructive and irreversible from this screen, the
// number is free (the caller holds the occurrences), and a mechanism should
// name what it is about to do. A count, not the list: the list would be
// redesigning MMS's screen rather than copying it.
function ApplyStep({
  template, startOn, chargeMinor, apply, setApply, converting, formatMoney,
}) {
  const formatDate = useDateFormat();
  const datePattern = useDatePattern();
  const priceMinor = salePriceMinor(chargeMinor, template);
  const notice = convertNotice(converting.length, startOn, datePattern);
  return (
    <>
      <div className={s.blurb}>Start date: {formatDate(startOn) || startOn}</div>

      <div className={t.wrap}>
        <table className={t.table}>
          <thead><tr><th>Item</th><th>Time</th><th>Price</th></tr></thead>
          <tbody>
            <tr>
              <td>{template?.name}</td>
              <td>{formatDuration(template?.minutes)}</td>
              <td>{formatMoney(priceMinor)}</td>
            </tr>
            <tr>
              <td />
              <td className={s.total}>Total:</td>
              <td>{formatMoney(priceMinor)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className={s.section}>
        <Field label="Apply Package Time">
          <div className={s.radioStack}>
            {applyOptions().map((o) => (
              <label key={o.id} className={s.radio}>
                <input
                  type="radio"
                  name="apply-package-time"
                  checked={apply === o.id}
                  onChange={() => setApply(o.id)}
                />
                {o.label}
              </label>
            ))}
          </div>
        </Field>
      </div>

      {notice ? <div className={s.notice}>{notice}</div> : null}
    </>
  );
}
